import React from "react";
import { Transition as TransitionBase } from "@headlessui/react";

import { transitionKeys } from "utils/style/transition";

interface Props {
  show: boolean;
  children?: React.ReactNode;
  appear?: boolean;
  unmount?: boolean;
  afterLeave?: () => void;
}

/**
 * A thin wrapper over HeadlessUI's Transition component. Headless version accepts
 * transition classes for transition points: 'enter', 'enterFrom', 'enterTo', 'leave',
 * 'leaveTo', 'leaveFrom'. Because we are using styled components and class names are
 * not explicitly defined by the user (ie they are auto-generated), we instead just set
 * the classes to reflect the name of the transition point (e.g. the value for 'enter' is
 * the class name 'enter') and target the class with a selector that includes styled
 * components auto-generated class name. For example, to set the transition duration on enter,
 * you'd defined some styled css like `&.enter: { transition: all 100ms ease-in-out; }`. "&"
 * is a special character in styled components that refers to the current styled components
 * class name.
 *
 * We've also created the `buildTransition` utility to help create transitions.
 *
 * ```
 * const fadeOpacity = buildTransition({
 *   enter: {
 *     transition: all 250ms ease;
 *   },
 *   enterFrom: {
 *     opacity: 0,
 *   },
 *   enterTo: {
 *     opacity: 1,
 *   }
 *   leave: {
 *     transition: all 250ms ease;
 *   },
 *   leaveFrom: {
 *     opacity: 1,
 *   },
 *   leaveTo: {
 *     opacity: 1,
 *   }
 * })
 *
 * const MyComponent = ({show}) => (<Transition show={show} css={fadeOpacity}><div>Hello World</div></Transition>);
 * ```
 *
 * If you are transitioning on a transform property or more than one child element, using `Transition.Child` as children
 * to your `Transition`, where the transition css is applied to the child as opposed to the parent.
 *
 * See https://headlessui.dev/react/transition for further details
 */
const Transition = ({ children, ...rest }: Props) => (
  <TransitionBase {...rest} {...transitionKeys}>
    {children}
  </TransitionBase>
);

const Child = (props: Omit<Props, "show">) => (
  <TransitionBase.Child as={React.Fragment} {...transitionKeys} {...props} />
);

Transition.Child = Child;

export default Transition;
